import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  CircularProgress,
  Chip,
  TextField,
  IconButton,
  Dialog,
  DialogContent,
  DialogActions,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import FileDownloadRoundedIcon from "@mui/icons-material/FileDownloadRounded";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import PhoneRoundedIcon from "@mui/icons-material/PhoneRounded";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import BadgeRoundedIcon from "@mui/icons-material/BadgeRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import { useNavigate } from "react-router-dom";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DataTable from "examples/Tables/DataTable";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import axios from "axios";
import { Notify } from "notiflix";
import { ENDPOINT } from "constants";
import { useSelector } from "react-redux";
import { buildSubmissionsWorkbook, downloadWorkbook, matchesSearch } from "./exportSubmissions";

const API_BASE = `${ENDPOINT}api/v1/admin/question-answer`;

const formatDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-AU", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// ── Submission progress status → label + colors ─────────────────────────────
const STATUS_META = {
  completed:    { label: "Completed",    color: "#16a34a", bg: "#eafaf0", border: "#bbe8cc" },
  in_progress:  { label: "In Progress",  color: "#b45309", bg: "#fff7ed", border: "#fcd9a8" },
  details_only: { label: "Details Only", color: "#6b7280", bg: "#f3f4f6", border: "#e0e2e8" },
};

const StatusChip = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.completed;
  return (
    <Chip
      label={meta.label}
      size="small"
      sx={{
        fontSize: 11,
        fontWeight: 600,
        color: meta.color,
        background: meta.bg,
        border: `1px solid ${meta.border}`,
        "& .MuiChip-label": { px: 1 },
      }}
    />
  );
};

// ── User type chip ───────────────────────────────────────────────────────────
// Logged-in submissions carry the account's userId → "Customer" (clickable, opens
// the customer's details). Guests (not logged in) → plain "Guest".
const UserTypeChip = ({ userId, onView }) => {
  if (!userId) {
    return (
      <Chip
        label="Guest"
        size="small"
        sx={{
          fontSize: 11,
          fontWeight: 600,
          color: "#6b7280",
          background: "#f3f4f6",
          border: "1px solid #e0e2e8",
          "& .MuiChip-label": { px: 1 },
        }}
      />
    );
  }
  return (
    <Tooltip title="View details" arrow>
      <Chip
        icon={<PersonRoundedIcon sx={{ fontSize: "15px !important", color: "#3854F6 !important" }} />}
        label="Customer"
        size="small"
        clickable
        onClick={() => onView?.(userId)}
        sx={{
          fontSize: 11,
          fontWeight: 600,
          color: "#3854F6",
          background: "#e8ecff",
          border: "1px solid #c7d0ff",
          cursor: "pointer",
          "&:hover": { background: "#dbe2ff" },
          "& .MuiChip-label": { px: 1 },
        }}
      />
    </Tooltip>
  );
};

// ── Meta row (icon + label + value) ─────────────────────────────────────────
const MetaItem = ({ icon: Icon, label, value, mono }) => (
  <MDBox display="flex" alignItems="flex-start" gap={1}>
    <MDBox
      sx={{
        width: 30, height: 30, borderRadius: "8px", flexShrink: 0,
        display: "flex", alignItems: "center", justifyContent: "center",
        background: "#e8ecff", color: "#3854F6",
      }}
    >
      <Icon sx={{ fontSize: 16 }} />
    </MDBox>
    <MDBox sx={{ minWidth: 0 }}>
      <MDTypography variant="caption" color="secondary" display="block" sx={{ fontSize: 10, lineHeight: 1.2, textTransform: "uppercase", letterSpacing: "0.06em" }}>
        {label}
      </MDTypography>
      <MDTypography
        variant="caption" fontWeight="medium" color="text"
        sx={{ fontSize: 13, wordBreak: "break-word", fontFamily: mono ? "monospace" : "inherit" }}
      >
        {value || "—"}
      </MDTypography>
    </MDBox>
  </MDBox>
);

// ── Answers detail modal ────────────────────────────────────────────────────
const AnswersModal = ({ open, submission, onClose, onViewUser }) => {
  if (!submission) return null;

  const answers = submission.answers || [];
  const questionnaireName =
    submission.questionnaire?.name || submission.questionnaireName || "Questionnaire";
  const pageRoute =
    submission.pageRoute || submission.questionnaire?.pageRoute || submission.questionnaire?.page?.route;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
    >
      {/* Header */}
      <MDBox
        px={3} py={2.5}
        sx={{ background: "linear-gradient(135deg, #3854F6 0%, #5b73ff 100%)", color: "#fff" }}
        display="flex" justifyContent="space-between" alignItems="flex-start"
      >
        <MDBox>
          <MDBox display="flex" alignItems="center" gap={1.5} mb={0.5}>
            <MDTypography variant="h5" color="white" fontWeight="bold">
              {questionnaireName}
            </MDTypography>
            <StatusChip status={submission.status} />
          </MDBox>
          <MDTypography variant="button" color="white" sx={{ opacity: 0.85 }}>
            {answers.length} {answers.length === 1 ? "answer" : "answers"} &bull;{" "}
            {formatDate(submission.submittedAt || submission.createdAt)}
          </MDTypography>
        </MDBox>
        <IconButton size="small" onClick={onClose} sx={{ color: "#fff" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </MDBox>

      {/* Respondent meta */}
      <MDBox
        px={3} py={2.5}
        sx={{ background: "#f8f9fc", borderBottom: "1px solid #eef0f5" }}
        display="grid"
        gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }}
        gap={2}
      >
        <MetaItem icon={PersonRoundedIcon} label="Name" value={submission.userName} />
        <MetaItem icon={EmailRoundedIcon} label="Email" value={submission.userEmail || submission.email} />
        <MetaItem icon={PhoneRoundedIcon} label="Phone" value={submission.userPhone} />
        <MetaItem icon={LinkRoundedIcon} label="Page Route" value={pageRoute} mono />
        <MetaItem
          icon={BadgeRoundedIcon}
          label="User Type"
          value={submission.userId ? "Customer" : "Guest — not logged in"}
        />
      </MDBox>

      <DialogContent sx={{ p: 3, background: "#fff" }}>
        <MDTypography variant="caption" fontWeight="bold" color="secondary" sx={{ textTransform: "uppercase", letterSpacing: "0.06em" }}>
          Questions &amp; Answers
        </MDTypography>

        {answers.length === 0 ? (
          <MDTypography variant="body2" color="secondary" textAlign="center" py={5}>
            No answers recorded for this submission.
          </MDTypography>
        ) : (
          <MDBox display="flex" flexDirection="column" gap={2} mt={1.5}>
            {answers.map((item, idx) => {
              const questionText =
                item.question?.text ||
                item.question?.questionText ||
                item.questionText ||
                (typeof item.question === "string" ? item.question : null) ||
                `Question ${idx + 1}`;

              const rawOptions =
                item.selectedOptions || item.options || item.answers || item.selectedOptionIds || [];

              const optionLabels = (Array.isArray(rawOptions) ? rawOptions : [rawOptions]).map(
                (o) => o?.text || o?.optionText || o?.label || o?.value || String(o)
              );

              return (
                <MDBox
                  key={item.questionId || idx}
                  sx={{
                    border: "1px solid #e8ebf2",
                    borderRadius: 2,
                    p: 2,
                    background: "#fff",
                  }}
                >
                  {/* Question */}
                  <MDBox display="flex" alignItems="flex-start" gap={1.5} mb={1.5}>
                    <MDBox
                      sx={{
                        minWidth: 26, height: 26, borderRadius: "50%", flexShrink: 0,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        background: "#3854F6", color: "#fff", fontSize: 12, fontWeight: 700,
                      }}
                    >
                      {idx + 1}
                    </MDBox>
                    <MDTypography variant="body2" fontWeight="medium" color="dark" sx={{ mt: 0.25 }}>
                      {questionText}
                    </MDTypography>
                  </MDBox>

                  {/* Selected answers */}
                  <MDBox pl={5}>
                    <MDTypography
                      variant="caption" color="secondary" display="block" mb={0.75}
                      sx={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em" }}
                    >
                      {optionLabels.length > 1 ? "Selected answers" : "Selected answer"}
                    </MDTypography>
                    <MDBox display="flex" flexWrap="wrap" gap={1}>
                      {optionLabels.map((label, i) => (
                        <Chip
                          key={i}
                          icon={<CheckCircleRoundedIcon sx={{ fontSize: "16px !important", color: "#16a34a !important" }} />}
                          label={label}
                          size="small"
                          sx={{
                            fontSize: 12,
                            fontWeight: 500,
                            color: "#0f172a",
                            background: "#eef6ee",
                            border: "1px solid #cde7cd",
                            "& .MuiChip-label": { px: 1 },
                          }}
                        />
                      ))}
                    </MDBox>
                  </MDBox>
                </MDBox>
              );
            })}
          </MDBox>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, justifyContent: "space-between" }}>
        {submission.userId ? (
          <MDButton
            size="small"
            variant="outlined"
            color="dark"
            startIcon={<OpenInNewRoundedIcon />}
            onClick={() => onViewUser?.(submission.userId)}
          >
            View User Details
          </MDButton>
        ) : (
          <MDBox />
        )}
        <MDButton size="small" variant="gradient" color="info" onClick={onClose}>
          Close
        </MDButton>
      </DialogActions>
    </Dialog>
  );
};

// ── Main page ────────────────────────────────────────────────────────────────
const QuestionnaireSubmissions = () => {
  const { user } = useSelector((state) => state.auth);
  const token = user?.accessToken;
  const navigate = useNavigate();

  // A "customer" in the admin is a User; the detail page lives at /customers/:id.
  const goToCustomer = (userId) => {
    if (userId) navigate(`/customers/${userId}`);
  };

  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [pagination, setPagination] = useState({ total: 0, page: 1, totalPages: 1 });
  const [selected, setSelected] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchSubmissions = useCallback(
    async (page = 1) => {
      try {
        setLoading(true);
        const { data } = await axios.get(`${API_BASE}/submissions`, {
          params: { page, limit: 20 },
          headers,
        });
        if (data?.success || data?.statusCode === 200) {
          setSubmissions(data.data?.submissions || data.data || []);
          setPagination(
            data.data?.pagination || { total: 0, page: 1, totalPages: 1 }
          );
        } else {
          Notify.failure(data?.message || "Failed to fetch submissions");
        }
      } catch (err) {
        Notify.failure(err?.response?.data?.message || "Error fetching submissions");
      } finally {
        setLoading(false);
      }
    },
    [token]
  );

  useEffect(() => {
    fetchSubmissions(pagination.page);
  }, [fetchSubmissions, pagination.page]);

  const handleViewAnswers = async (submission) => {
    // If answers are already embedded in the list response, open directly
    if (submission.answers && submission.answers.length > 0) {
      setSelected(submission);
      setModalOpen(true);
      return;
    }
    // Otherwise fetch the full detail
    try {
      setDetailLoading(true);
      const { data } = await axios.get(`${API_BASE}/submissions/${submission.id}`, { headers });
      const detail = data?.data || submission;
      setSelected({ ...submission, ...detail });
      setModalOpen(true);
    } catch (err) {
      Notify.failure(err?.response?.data?.message || "Failed to load submission details");
      setSelected(submission);
      setModalOpen(true);
    } finally {
      setDetailLoading(false);
    }
  };

  // Export every submission (not just the page on screen) to an Excel file. The table search is
  // applied too, so a filtered view exports only the matching rows.
  const handleExport = async () => {
    try {
      setExporting(true);
      const all = [];
      let page = 1;
      let totalPages = 1;
      do {
        const { data } = await axios.get(`${API_BASE}/submissions`, {
          params: { page, limit: 200 },
          headers,
        });
        const list = data?.data?.submissions || data?.data || [];
        all.push(...list);
        totalPages = data?.data?.pagination?.totalPages || 1;
        page += 1;
      } while (page <= totalPages);

      const rowsToExport = all.filter((s) => matchesSearch(s, search));
      if (rowsToExport.length === 0) {
        Notify.warning("No submissions to export.");
        return;
      }
      const { wb, submissionCount, answerCount } = buildSubmissionsWorkbook(rowsToExport);
      downloadWorkbook(wb);
      Notify.success(`Exported ${submissionCount} submissions (${answerCount} answers).`);
    } catch (err) {
      Notify.failure(err?.response?.data?.message || "Failed to export submissions");
    } finally {
      setExporting(false);
    }
  };

  const filtered = search.trim() ? submissions.filter((s) => matchesSearch(s, search)) : submissions;

  const columns = [
    { Header: "#",             accessor: "serial",  width: "4%"  },
    { Header: "Questionnaire", accessor: "name",    width: "16%" },
    { Header: "User",          accessor: "user",     width: "16%" },
    { Header: "User Type",     accessor: "userType", align: "center", width: "9%" },
    { Header: "Page Route",    accessor: "route",   width: "13%" },
    { Header: "Status",        accessor: "status",  align: "center", width: "10%" },
    { Header: "Answers",       accessor: "count",   align: "center", width: "8%"  },
    { Header: "Submitted At",  accessor: "date",    width: "13%" },
    { Header: "Actions",       accessor: "actions", align: "center", width: "6%"  },
  ];

  const rows = filtered.map((s, idx) => ({
    serial: (
      <MDTypography variant="caption" color="secondary">
        {(pagination.page - 1) * 20 + idx + 1}
      </MDTypography>
    ),
    name: (
      <MDTypography variant="caption" fontWeight="medium">
        {s.questionnaire?.name || s.questionnaireName || "—"}
      </MDTypography>
    ),
    user: (
      <MDBox>
        {(s.userName) && (
          <MDTypography variant="caption" fontWeight="medium" display="block">
            {s.userName}
          </MDTypography>
        )}
        <MDTypography variant="caption" color="text">
          {s.userEmail || "—"}
        </MDTypography>
        {s.userPhone && (
          <MDTypography variant="caption" color="secondary" display="block">
            {s.userPhone}
          </MDTypography>
        )}
      </MDBox>
    ),
    userType: <UserTypeChip userId={s.userId} onView={goToCustomer} />,
    route: (
      <MDTypography variant="caption" color="text" sx={{ fontFamily: "monospace" }}>
        {s.questionnaire?.pageRoute || s.pageRoute || "—"}
      </MDTypography>
    ),
    status: <StatusChip status={s.status} />,
    email: (
      <MDTypography variant="caption" color="text">
        {s.userEmail || "—"}
      </MDTypography>
    ),
    count: (
      <Chip
        label={`${s.answers?.length ?? s.answersCount ?? "?"} answers`}
        size="small"
        variant="outlined"
        color="info"
        sx={{ fontSize: 11 }}
      />
    ),
    date: (
      <MDTypography variant="caption" color="text">
        {formatDate(s.submittedAt || s.createdAt)}
      </MDTypography>
    ),
    actions: (
      <MDButton
        size="small"
        variant="outlined"
        color="info"
        disabled={detailLoading}
        onClick={() => handleViewAnswers(s)}
      >
        View
      </MDButton>
    ),
  }));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Card>
          {/* Header */}
          <MDBox
            mx={2} mt={-3} py={3} px={2}
            variant="gradient" bgColor="info"
            borderRadius="lg" coloredShadow="info"
            display="flex" justifyContent="space-between" alignItems="center"
          >
            <MDTypography variant="h6" color="white">
              Questionnaire Submissions
              {pagination.total > 0 && (
                <MDTypography
                  component="span" variant="caption" color="white"
                  sx={{ ml: 1, opacity: 0.8 }}
                >
                  ({pagination.total} total)
                </MDTypography>
              )}
            </MDTypography>
            <MDBox display="flex" alignItems="center" gap={1}>
              <Tooltip title="Download all submissions (and their answers) as an Excel file" arrow>
                <span>
                  <MDButton
                    size="small"
                    variant="contained"
                    color="white"
                    disabled={exporting || loading}
                    onClick={handleExport}
                    startIcon={
                      exporting ? (
                        <CircularProgress size={14} color="inherit" />
                      ) : (
                        <FileDownloadRoundedIcon fontSize="small" />
                      )
                    }
                  >
                    {exporting ? "Exporting…" : "Export Excel"}
                  </MDButton>
                </span>
              </Tooltip>
              <IconButton
                size="small"
                onClick={() => fetchSubmissions(1)}
                sx={{ color: "white" }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </MDBox>
          </MDBox>

          {/* Search */}
          <MDBox px={3} pt={3} pb={1}>
            <TextField
              size="small"
              placeholder="Search by questionnaire name, email, or page route…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: 340 }}
              InputProps={{
                endAdornment: (
                  <IconButton size="small" disableRipple>
                    <SearchIcon fontSize="small" />
                  </IconButton>
                ),
              }}
            />
          </MDBox>

          {/* Table */}
          <MDBox px={2} pb={2}>
            {loading ? (
              <MDBox display="flex" justifyContent="center" py={6}>
                <CircularProgress />
              </MDBox>
            ) : filtered.length === 0 ? (
              <MDBox py={6} textAlign="center">
                <MDTypography variant="body2" color="secondary">
                  No submissions found.
                </MDTypography>
              </MDBox>
            ) : (
              <DataTable
                table={{ columns, rows }}
                isSorted={false}
                entriesPerPage={false}
                showTotalEntries={false}
                canSearch={false}
                noEndBorder
              />
            )}
          </MDBox>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <MDBox
              px={3} pb={3}
              display="flex" justifyContent="flex-end" alignItems="center" gap={1}
            >
              <MDButton
                size="small" variant="outlined" color="info"
                disabled={pagination.page <= 1}
                onClick={() => setPagination((p) => ({ ...p, page: p.page - 1 }))}
              >
                Prev
              </MDButton>
              <MDTypography variant="caption" color="text">
                Page {pagination.page} of {pagination.totalPages}
              </MDTypography>
              <MDButton
                size="small" variant="outlined" color="info"
                disabled={pagination.page >= pagination.totalPages}
                onClick={() => setPagination((p) => ({ ...p, page: p.page + 1 }))}
              >
                Next
              </MDButton>
            </MDBox>
          )}
        </Card>
      </MDBox>

      <AnswersModal
        open={modalOpen}
        submission={selected}
        onClose={() => { setModalOpen(false); setSelected(null); }}
        onViewUser={goToCustomer}
      />

      <Footer />
    </DashboardLayout>
  );
};

export default QuestionnaireSubmissions;
